import { useCallback, useEffect } from 'react';
import { DateTime } from 'luxon';
import { useGetTimesheetPeriods } from 'modules/smartDiary/timesheetPeriod/hooks';
import { Period, SortOrder } from 'graphql/types';
import {
  DateRange,
  useSmartTimesheetContext
} from '../SmartTimesheetContextProvider';

type ReturnType = {
  loadMore: LoadMorePeriodType;
  loading: boolean;
};
export type LoadMorePeriodType = (args: DateRange) => Promise<Period[]>;

type UseTimesheetPeriodArgs = {
  fromDate?: DateTime;
  toDate?: DateTime;
};
export const useTimesheetPeriod = ({
  fromDate = DateTime.local().minus({ months: 2 }),
  toDate = DateTime.local()
}: UseTimesheetPeriodArgs = {}): ReturnType => {
  const {
    timesheetPeriods,
    setTimesheetPeriods,
    displayTimesheetPeriods,
    setCurrentPeriod
  } = useSmartTimesheetContext();
  const { loading, periods, fetchMore } = useGetTimesheetPeriods(
    fromDate.startOf('day'),
    toDate.endOf('day')
  );

  useEffect(() => {
    if (!loading && periods?.length && !timesheetPeriods.length) {
      setTimesheetPeriods(periods);
      if (displayTimesheetPeriods) {
        setCurrentPeriod(periods[periods.length - 1]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, periods]);

  const loadMore = useCallback(
    async ({ startDate, endDate }: DateRange): Promise<Period[]> => {
      const { data } = await fetchMore({
        variables: {
          input: {
            filter: {
              fromDate: startDate.toISODate(),
              toDate: endDate.toISODate()
            },
            count: Math.ceil(endDate.diff(startDate, 'days').days),
            sort: SortOrder.Ascending
          }
        }
      });

      if (data.periods?.items.length) {
        const mergedPeriods = [...data.periods.items, ...timesheetPeriods];
        setTimesheetPeriods(mergedPeriods);
        return mergedPeriods;
      }
      return data.periods?.items ?? [];
    },
    [fetchMore, setTimesheetPeriods, timesheetPeriods]
  );
  return { loading, loadMore };
};
